import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'

import useCachedData from 'hooks/cached_data'
import useDocumentTitle from 'hooks/document_title'
import type {RootState} from 'store/actions'
import isMobileVersion from 'store/mobile'
import {parseQueryString} from 'store/parse'

import Button from 'components/button'
import {colorToAlpha} from 'components/colors'
import Trans from 'components/i18n_trans'

import type {ValidSection} from './section'

import {getUpskillingSections} from '../../store/actions'
import {fullContext} from '../departement'
import {horizontalPagePadding, verticalPagePadding} from '../padding'
import type {Inputable} from '../search_section'
import SearchSection from '../search_section'
import Section from '../section'
import Stars from '../stars'

const containerStyle: React.CSSProperties = {
  backgroundColor: colors.BACKGROUND,
}

const subtitleStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  // TODO(pascal): Consider dropping the search on mobile.
  flexDirection: isMobileVersion ? 'column' : 'row',
  fontSize: isMobileVersion ? 16 : 20,
  fontWeight: 'normal',
  marginBottom: '0 0 50px',
  padding: '0px 25px',
}
const sectionStyle: React.CSSProperties = {
  marginBottom: 30,
  marginTop: 30,
}
const spacerStyle: React.CSSProperties = {
  flex: '1 0 15px',
}
// TODO(pascal): Move to button style.
const searchButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: `1px solid ${colors.PINKISH_GREY_TWO}`,
  boxShadow: 'none',
  color: colors.TEXT,
  display: 'inline-flex',
  fontSize: 14,
  fontWeight: 'bold',
  margin: '0 0 0 10px',
  padding: isMobileVersion ? 8 : 10,
  textShadow: 'none',
}

const selectionSectionStyle: React.CSSProperties = {
  ...sectionStyle,
  padding: 25,
}

const noPageMarginStyle: React.CSSProperties = {
  margin: `0 -${horizontalPagePadding}px -${verticalPagePadding}px -${horizontalPagePadding}px`,
}

const separatorStyle: React.CSSProperties = {
  border: 0,
  borderTop: `solid 1px ${colorToAlpha('#fff', .2)}`,
  margin: '15px 25px 10px',
}

const isSearchEnabled = config.isSearchEnabled || !!parseQueryString(window.location.search).search

const NetflixPage = (): React.ReactElement => {
  const {t} = useTranslation()
  const [isSearchShown, setIsSearchShown] = useState(false)
  const toggleSearch = useCallback(() => setIsSearchShown((wasShown: boolean) => !wasShown), [])
  const departementId = useSelector(({user}: RootState) => user?.projects?.[0]?.city?.departementId)
  const sectionsAction = useMemo(() => getUpskillingSections(departementId), [departementId])
  const {data: sections = []} = useCachedData(
    ({app: {upskillingSections}}) => upskillingSections?.[departementId || ''],
    sectionsAction,
  )
  const selectedJobs = useSelector(({app: {upskillingSelectedJobs}}: RootState) =>
    upskillingSelectedJobs) || []
  const realSections = sections.filter((section): section is ValidSection => !!section.id)
  const hasFeedbackBanner = config.hasFeedbackBanner
  // eslint-disable-next-line max-len
  // i18next-extract-mark-context-next-line ["career_fr_DEPARTEMENT", "career_uk_CITY", "promising-job_fr_CITY"]
  const pitch = t('Découvrez les meilleures carrières dans votre département', fullContext)
  useDocumentTitle(`${config.productName} | ${pitch}`)
  const searchRef = useRef<Inputable>(null)
  useEffect(() => {
    if (isSearchShown) {
      searchRef.current?.focus()
    }
  }, [isSearchShown])
  return <div style={containerStyle}>
    <h1 style={subtitleStyle}>
      <div>
        {config.mainTitle ?
          // i18next-extract-disable-next-line
          <Trans parent={null}>{config.mainTitle}</Trans> :
          // i18next-extract-mark-context-next-line ["career", "promising-job"]
          <Trans parent={null} tOptions={{context: config.goalWordingContext}}>
            Voici les <strong>meilleurs métiers</strong> de votre département pour vous donner
            des <strong>idées de carrière</strong>&nbsp;!{' '}
            <span aria-hidden={true}>🙌</span>
          </Trans>
        }
      </div>
      {isSearchEnabled ? <React.Fragment>
        <span style={spacerStyle} />
        <Button onClick={toggleSearch} aria-expanded={isSearchShown} style={searchButtonStyle}>
          {isSearchShown ? t('Masquer la recherche') : t('Recherche')}
        </Button>
      </React.Fragment> : null}
    </h1>
    {isSearchShown ? <SearchSection ref={searchRef} /> : null}
    {selectedJobs?.length ? <Section
      id="selection" jobs={selectedJobs} name={t('Vos métiers favoris')}
      style={selectionSectionStyle} /> : <React.Fragment>
      <hr style={separatorStyle} />
      <div style={{height: 1}} /></React.Fragment>}
    {realSections.map((section, index) => {
      return <React.Fragment key={index}>
        <Section style={sectionStyle} key={section.id} {...section} />
        {hasFeedbackBanner && index === 0 ? <Section
          style={sectionStyle} key="feedback" {...section} isFeedbackBanner={true}
          jobs={undefined} /> : null}
      </React.Fragment>
    })}
    {realSections.length ? <Stars style={noPageMarginStyle} /> : null}
  </div>
}

export default React.memo(NetflixPage)
